import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';
import type { CreateTandaRequest } from '../types';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX');
}

export function TandasPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateTandaRequest>({
    name: '',
    amount_per_turn: 0,
    frequency: 'weekly',
    total_participants: 0,
    start_date: '',
  });

  const { data: tandas, isLoading } = useQuery({
    queryKey: ['tandas'],
    queryFn: () => api.getTandas(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateTandaRequest) => api.createTanda(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tandas'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowForm(false);
      setFormData({ name: '', amount_per_turn: 0, frequency: 'weekly', total_participants: 0, start_date: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.amount_per_turn > 0 && formData.total_participants > 0) {
      createMutation.mutate(formData);
    }
  };

  const activeTandas = tandas?.filter((t) => t.status === 'active') || [];
  const totalMonthly = activeTandas.reduce((sum, t) => {
    const multiplier = t.frequency === 'weekly' ? 4 : t.frequency === 'biweekly' ? 2 : 1;
    return sum + t.amount_per_turn * multiplier;
  }, 0);

  const frequencyLabels: Record<string, string> = {
    weekly: 'Semanal',
    biweekly: 'Quincenal',
    monthly: 'Mensual',
  };

  return (
    <div>
      <Header title="Tandas" />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="text-2xl mb-2">👥</div>
          <div className="stat-value">{activeTandas.length}</div>
          <div className="stat-label">Tandas activas</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">💰</div>
          <div className="stat-value">{formatMoney(totalMonthly)}</div>
          <div className="stat-label">Compromiso mensual</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">📅</div>
          <div className="stat-value">
            {activeTandas.reduce((sum, t) => sum + t.total_participants, 0)}
          </div>
          <div className="stat-label">Participantes totales</div>
        </div>
      </div>

      {/* Add Button / Form */}
      <div className="card mb-6">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            ➕ Nueva Tanda
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold text-primary-500">Nueva Tanda</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre de la tanda</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ej: Tanda del trabajo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Monto por turno</label>
                <input
                  type="number"
                  className="input"
                  step="100"
                  value={formData.amount_per_turn || ''}
                  onChange={(e) => setFormData({ ...formData, amount_per_turn: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <label className="label">Frecuencia</label>
                <select
                  className="input"
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as 'weekly' | 'biweekly' | 'monthly' })}
                >
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quincenal</option>
                  <option value="monthly">Mensual</option>
                </select>
              </div>
              <div>
                <label className="label">Número de participantes</label>
                <input
                  type="number"
                  className="input"
                  min="2"
                  value={formData.total_participants || ''}
                  onChange={(e) => setFormData({ ...formData, total_participants: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <label className="label">Fecha de inicio</label>
                <input
                  type="date"
                  className="input"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Guardando...' : 'Crear Tanda'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Tandas List */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">📋 Mis Tandas</h2>
        {isLoading ? (
          <p className="text-gray-500 text-center py-4">Cargando...</p>
        ) : (
          <div className="space-y-3">
            {tandas?.map((tanda) => (
              <div key={tanda.id} className="p-4 bg-dark-bg rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-semibold text-lg">{tanda.name}</span>
                  <span className={`badge ${tanda.status === 'active' ? 'badge-success' : tanda.status === 'completed' ? 'badge-warning' : 'badge-danger'}`}>
                    {tanda.status.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-500">
                      {frequencyLabels[tanda.frequency]} • {tanda.total_participants} participantes
                    </div>
                    <div className="text-sm text-gray-500">
                      Turno {tanda.current_turn} de {tanda.total_participants}
                    </div>
                    <div className="text-sm text-gray-500">
                      Inicia: {formatDate(tanda.start_date)}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-primary-500">
                    {formatMoney(tanda.amount_per_turn)}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${(tanda.current_turn / tanda.total_participants) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
            {!tandas?.length && (
              <p className="text-gray-500 text-center py-4">No hay tandas registradas</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
